import type { OfferModel } from "./types/models/offer";
import type { CampaignBlock } from "./types/models/campaign-block";
import { getEffectivePrice } from "./pricing";
import { CAMPAIGN_BLOCKS_BY_OFFER_TYPE, isOfferLive, type LiveOffer } from "./offers";

type Offer = OfferModel.Offer;
type OfferType = OfferModel.OfferType;

// ── Status ──────────────────────────────────────────────────────────────────

export type CampaignStatus = "draft" | "scheduled" | "live" | "paused" | "ended";

/**
 * The status shown to merchants, derived from the stored status and dates (no backend change):
 * Ended = past ends_at; Scheduled = active, starts later; Live = active inside the window;
 * Paused = inactive inside the window; Draft = inactive and not started yet (or no dates).
 */
export const campaignStatus = (
  offer: Pick<Offer, "status" | "starts_at" | "ends_at">,
  now = Date.now()
): CampaignStatus => {
  const start = offer.starts_at ? Date.parse(offer.starts_at) : NaN;
  const end = offer.ends_at ? Date.parse(offer.ends_at) : NaN;
  if (!Number.isNaN(end) && end < now) return "ended";
  const started = !Number.isNaN(start) && start <= now;
  if (offer.status === "active") return Number.isNaN(start) || started ? "live" : "scheduled";
  return started ? "paused" : "draft";
};

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  live: "Live",
  paused: "Paused",
  ended: "Ended",
};

// ── Offer types explained ───────────────────────────────────────────────────

export const OFFER_TYPE_GUIDE: Record<OfferType, { label: string; summary: string; example: string }> = {
  single_product: {
    label: "Product discount",
    summary: "One product at a lower price for a limited time.",
    example: "Wireless earbuds 20% off this week",
  },
  buy_x_get_y: {
    label: "Buy X, get Y",
    summary: "Customers who buy some products get others free or cheaper.",
    example: "Buy 2 oats, get 1 free",
  },
  volume_discount: {
    label: "Bulk discount",
    summary: "The more of a product they buy, the bigger the discount.",
    example: "Buy 3 for 10% off, 5 or more for 20% off",
  },
  combo_fixed_price: {
    label: "Combo deal",
    summary: "A set of products sold together for one fixed price.",
    example: "Shampoo + conditioner + mask for TK 900",
  },
};

// ── Live price example ──────────────────────────────────────────────────────

/** Regular unit prices the example is computed from: per product (its first variation) and per variation. */
export interface PriceBook {
  byProduct: Record<string, number>;
  byVariation: Record<string, number>;
}

export interface PriceExample {
  /** What the example basket costs without the offer. */
  regular: number;
  /** What the customer pays with the offer. */
  pays: number;
  saving: number;
  /** Plain-language description of the example basket ("Buying 3"). */
  basket: string;
}

const example = (regular: number, pays: number, basket: string): PriceExample => ({
  regular,
  pays: Math.min(pays, regular),
  saving: Math.max(0, regular - pays),
  basket,
});

/**
 * Example baskets with and without the offer, priced with the storefront's pricing code. Returns one
 * example per volume tier and one for every other type; empty when the prices needed are not known yet.
 */
export const priceExamples = (type: OfferType, rules: Partial<OfferModel.OfferRules>, prices: PriceBook): PriceExample[] => {
  const r = rules as Record<string, unknown>;
  switch (type) {
    case "single_product": {
      const unit = prices.byProduct[String(r.product_id ?? "")];
      if (unit === undefined) return [];
      return [example(unit, getEffectivePrice(unit, Number(r.discount_value) || 0, String(r.discount_type ?? "")), "1 item")];
    }
    case "volume_discount": {
      const unit = prices.byProduct[String(r.product_id ?? "")];
      const tiers = (r.tiers as OfferModel.VolumeDiscountRules["tiers"] | undefined) ?? [];
      if (unit === undefined) return [];
      return tiers
        .filter((t) => t.min_quantity > 0)
        .map((t) => {
          const qty = t.min_quantity;
          return example(unit * qty, getEffectivePrice(unit, t.discount_value || 0, t.discount_type) * qty, `${qty} items`);
        });
    }
    case "combo_fixed_price": {
      const items = (r.items as OfferModel.ComboFixedPriceRules["items"] | undefined) ?? [];
      if (!items.length || items.some((i) => prices.byVariation[i.product_variation_id] === undefined)) return [];
      const regular = items.reduce((sum, i) => sum + prices.byVariation[i.product_variation_id]! * (i.quantity || 1), 0);
      const count = items.reduce((sum, i) => sum + (i.quantity || 1), 0);
      return [example(regular, Number(r.price) || 0, `the ${count}-item combo`)];
    }
    case "buy_x_get_y": {
      const buy = (r.buy_items as OfferModel.BuyXGetYRules["buy_items"] | undefined) ?? [];
      const get = (r.get_items as OfferModel.BuyXGetYRules["get_items"] | undefined) ?? [];
      if (!buy.length || !get.length) return [];
      if (buy.some((b) => prices.byProduct[b.product_id] === undefined)) return [];
      if (get.some((g) => prices.byVariation[g.product_variation_id] === undefined)) return [];
      const buyTotal = buy.reduce((sum, b) => sum + prices.byProduct[b.product_id]! * (b.quantity || 1), 0);
      const getRegular = get.reduce((sum, g) => sum + prices.byVariation[g.product_variation_id]! * (g.quantity || 1), 0);
      const getPays = get.reduce(
        (sum, g) => sum + getEffectivePrice(prices.byVariation[g.product_variation_id]!, g.discount_value || 0, g.discount_type) * (g.quantity || 1),
        0
      );
      const buyCount = buy.reduce((s, b) => s + (b.quantity || 1), 0);
      const getCount = get.reduce((s, g) => s + (g.quantity || 1), 0);
      return [example(buyTotal + getRegular, buyTotal + getPays, `${buyCount} bought + ${getCount} received`)];
    }
    default:
      return [];
  }
};

// ── Pre-launch checklist ────────────────────────────────────────────────────

export interface ChecklistInput {
  name: string;
  type: OfferType | null;
  rules: Partial<OfferModel.OfferRules>;
  starts_at: string;
  ends_at: string;
  max_total_uses: number | null;
  blocks: CampaignBlock[];
  /** Stock on hand per product id and per variation id (unknown ids are not checked). */
  stock?: { byProduct: Record<string, number>; byVariation: Record<string, number> };
  /** Whether the products have at least one image, per product id. */
  hasImages?: Record<string, boolean>;
  mobileReviewed?: boolean;
}

export interface ChecklistItem {
  id: string;
  label: string;
  /** "error" items block publishing; "warning" items are advice. */
  level: "error" | "warning";
  ok: boolean;
  detail?: string;
}

/** Product and variation ids referenced by draft rules (product ids for buy-side, variations for get/combo). */
export const ruleTargets = (rules: Partial<OfferModel.OfferRules>): { productIds: string[]; variationIds: string[] } => {
  const r = rules as Record<string, unknown>;
  const productIds = new Set<string>();
  const variationIds = new Set<string>();
  if (typeof r.product_id === "string" && r.product_id) productIds.add(r.product_id);
  for (const b of (r.buy_items as { product_id?: string }[] | undefined) ?? []) if (b.product_id) productIds.add(b.product_id);
  for (const g of (r.get_items as { product_variation_id?: string }[] | undefined) ?? []) if (g.product_variation_id) variationIds.add(g.product_variation_id);
  for (const i of (r.items as { product_id?: string; product_variation_id?: string }[] | undefined) ?? []) {
    if (i.product_id) productIds.add(i.product_id);
    if (i.product_variation_id) variationIds.add(i.product_variation_id);
  }
  return { productIds: [...productIds], variationIds: [...variationIds] };
};

/** Checks a campaign before launch: errors block publishing, warnings are shown but allowed. */
export const campaignChecklist = (input: ChecklistInput, now = Date.now()): ChecklistItem[] => {
  const items: ChecklistItem[] = [];
  const { productIds, variationIds } = ruleTargets(input.rules);
  const start = Date.parse(input.starts_at);
  const end = Date.parse(input.ends_at);

  items.push({ id: "name", label: "Campaign has a name", level: "error", ok: input.name.trim().length > 0 });
  items.push({ id: "products", label: "Offer has its products", level: "error", ok: productIds.length + variationIds.length > 0 });

  const datesValid = !Number.isNaN(start) && !Number.isNaN(end) && start < end;
  items.push({ id: "dates", label: "Start and end dates are valid", level: "error", ok: datesValid });
  if (datesValid) items.push({ id: "future", label: "End date is in the future", level: "error", ok: end > now });

  const r = input.rules as Record<string, unknown>;
  const priced =
    input.type === "combo_fixed_price" ? Number(r.price) > 0
    : input.type === "single_product" ? Number(r.discount_value) > 0
    : input.type === "volume_discount" ? ((r.tiers as unknown[] | undefined) ?? []).length > 0
    : input.type === "buy_x_get_y" ? ((r.get_items as unknown[] | undefined) ?? []).length > 0
    : false;
  items.push({ id: "price", label: "Offer price or discount is set", level: "error", ok: priced });

  if (input.stock) {
    const stocks = [
      ...productIds.map((id) => input.stock!.byProduct[id]),
      ...variationIds.map((id) => input.stock!.byVariation[id]),
    ].filter((s): s is number => s !== undefined);
    const outOfStock = stocks.some((s) => s <= 0);
    items.push({ id: "in-stock", label: "Products are in stock", level: "error", ok: !outOfStock });
    if (!outOfStock && input.max_total_uses && stocks.length) {
      const lowest = Math.min(...stocks);
      items.push({
        id: "stock-covers",
        label: "Stock covers the usage limit",
        level: "warning",
        ok: lowest >= input.max_total_uses,
        detail: lowest < input.max_total_uses ? `Lowest stock is ${lowest}, the limit is ${input.max_total_uses} uses` : undefined,
      });
    }
  }
  if (input.hasImages) {
    items.push({
      id: "images",
      label: "Products have images",
      level: "warning",
      ok: productIds.every((id) => input.hasImages![id] !== false),
    });
  }
  const types = new Set(input.blocks.map((b) => b.type));
  items.push({ id: "hero", label: "Page has a hero", level: "warning", ok: types.has("hero") });
  items.push({
    id: "hero-image",
    label: "Hero has an image",
    level: "warning",
    ok: input.blocks.some((b) => b.type === "hero" && typeof b.props.image === "string" && b.props.image.length > 0),
  });
  items.push({ id: "mobile", label: "Mobile view reviewed", level: "warning", ok: Boolean(input.mobileReviewed) });
  return items;
};

export const canPublish = (items: ChecklistItem[]): boolean => items.every((i) => i.ok || i.level !== "error");

// ── Templates ───────────────────────────────────────────────────────────────

export interface CampaignTemplate {
  id: string;
  label: string;
  description: string;
  offerType: OfferType;
  /** Block list without ids (ids are assigned when the template is applied). */
  blocks: Omit<CampaignBlock, "id">[];
}

const hero = (title: string, badge: string, description: string, variant = "v1"): Omit<CampaignBlock, "id"> => ({
  type: "hero",
  variant,
  props: { title, badge_text: badge, description },
});
const countdown = { type: "countdown", variant: "v1", props: { label: "Offer ends in" } };
const buyBox = (variant = "v1") => ({ type: "buy-box", variant, props: {} });
const faq = (items: { question: string; answer: string }[]) => ({
  type: "faq",
  variant: "v1",
  props: { title: "Frequently asked questions", items },
});
const DELIVERY_FAQ = { question: "How fast is delivery?", answer: "Orders are delivered in 2–5 working days. You pay when the order arrives." };

export const CAMPAIGN_TEMPLATES: CampaignTemplate[] = [
  {
    id: "flash-sale",
    label: "Flash sale",
    description: "Big hero, countdown and a quick order form.",
    offerType: "single_product",
    blocks: [
      hero("Flash sale", "LIMITED TIME", "Our best price of the season, only until the timer runs out."),
      countdown,
      { type: "product-grid", variant: "v1", props: { title: "On sale now", layout: "showcase" } },
      buyBox("v2"),
      faq([DELIVERY_FAQ]),
    ],
  },
  {
    id: "buy-2-get-1",
    label: "Buy 2 get 1",
    description: "Explains what to buy and what they get, then the order form.",
    offerType: "buy_x_get_y",
    blocks: [
      hero("Buy more, get more", "FREE GIFT", "Add the qualifying products and the reward is added to your order."),
      { type: "buy-items-list", variant: "v1", props: { title: "Buy these" } },
      { type: "get-items-showcase", variant: "v1", props: { title: "And you get" } },
      buyBox(),
      countdown,
      faq([{ question: "How do I get the free item?", answer: "It is added automatically when you order the qualifying products here." }, DELIVERY_FAQ]),
    ],
  },
  {
    id: "bulk-discount",
    label: "Bulk discount",
    description: "Tier table front and centre to raise order size.",
    offerType: "volume_discount",
    blocks: [
      hero("Buy more, save more", "BULK DEAL", "The discount grows with every item you add.", "v2"),
      { type: "discount-tiers-table", variant: "v1", props: { title: "Your discount" } },
      { type: "product-grid", variant: "v2", props: { title: "The product", layout: "list" } },
      buyBox(),
      faq([DELIVERY_FAQ]),
    ],
  },
  {
    id: "combo-deal",
    label: "Combo deal",
    description: "Shows the bundle saving with the products in the set.",
    offerType: "combo_fixed_price",
    blocks: [
      hero("The complete set", "COMBO", "Everything you need together, for one price."),
      { type: "bundle-price-callout", variant: "v1", props: { label: "Combo price" } },
      { type: "product-grid", variant: "v1", props: { title: "What's in the combo", layout: "grid" } },
      buyBox(),
      countdown,
      { type: "cta-banner", variant: "v1", props: { text: "Limited stock at this price", button_label: "Order the combo", button_link: "#buy" } },
    ],
  },
];

/** Templates usable for an offer type: its own templates first, then others filtered to blocks it supports. */
export const templatesFor = (type: OfferType, saved: CampaignTemplate[] = []): CampaignTemplate[] => {
  const allowed = new Set(CAMPAIGN_BLOCKS_BY_OFFER_TYPE[type]);
  return [...saved, ...CAMPAIGN_TEMPLATES]
    .sort((a, b) => Number(b.offerType === type) - Number(a.offerType === type))
    .map((t) => ({ ...t, blocks: t.blocks.filter((b) => allowed.has(b.type)) }))
    .filter((t) => t.blocks.length > 0);
};

/** A template's blocks with fresh ids, ready to store in ui_content.blocks. */
export const applyTemplate = (template: CampaignTemplate, makeId: () => string): CampaignBlock[] =>
  template.blocks.map((b) => ({ ...structuredClone(b), id: makeId() }));

/** Saves a campaign page as a reusable template (ids are dropped). */
export const templateFromBlocks = (label: string, offerType: OfferType, blocks: CampaignBlock[]): CampaignTemplate => ({
  id: `saved-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "template"}`,
  label,
  description: "Saved from a campaign",
  offerType,
  blocks: blocks.map(({ id: _id, ...rest }) => structuredClone(rest)),
});

// ── Duplicate ───────────────────────────────────────────────────────────────

/** Create-body for a copy of a campaign: renamed, switched off, same rules and page with fresh block ids. */
export const duplicateCampaignBody = (offer: Offer, makeId: () => string) => ({
  name: `${offer.name} (copy)`,
  status: "inactive" as const,
  type: offer.type,
  offer_rules: structuredClone(offer.offer_rules),
  starts_at: offer.starts_at,
  ends_at: offer.ends_at,
  min_order_value: offer.min_order_value,
  coupon_allowed: offer.coupon_allowed,
  is_campaign: true,
  ui_content: offer.ui_content
    ? { ...structuredClone(offer.ui_content), blocks: offer.ui_content.blocks?.map((b) => ({ ...structuredClone(b), id: makeId() })) }
    : null,
});

// ── Sharing kit ─────────────────────────────────────────────────────────────

export type ShareChannel = "facebook" | "whatsapp" | "sms" | "instagram" | "email";

const UTM_MEDIUM: Record<ShareChannel, string> = {
  facebook: "social",
  instagram: "social",
  whatsapp: "messaging",
  sms: "sms",
  email: "email",
};

/** Public URL of a campaign page. */
export const campaignUrl = (domain: string, slug: string): string =>
  `https://${domain.replace(/^https?:\/\//, "").replace(/\/+$/, "")}/campaign/${encodeURIComponent(slug)}`;

/** The campaign URL tagged for one channel (utm_source/medium/campaign). */
export const utmLink = (url: string, channel: ShareChannel, campaign: string): string => {
  const u = new URL(url);
  u.searchParams.set("utm_source", channel);
  u.searchParams.set("utm_medium", UTM_MEDIUM[channel]);
  u.searchParams.set("utm_campaign", campaign);
  return u.toString();
};

/** Ready-to-open share links (WhatsApp and Facebook share dialogs, an SMS draft). */
export const shareIntents = (url: string, message: string, campaign: string) => ({
  whatsapp: `https://wa.me/?text=${encodeURIComponent(`${message} ${utmLink(url, "whatsapp", campaign)}`)}`,
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(utmLink(url, "facebook", campaign))}`,
  sms: `sms:?&body=${encodeURIComponent(`${message} ${utmLink(url, "sms", campaign)}`)}`,
});

// ── Store-wide promotion ────────────────────────────────────────────────────

/** Where the merchant chose to promote a campaign (stored additively in ui_content.promote; absent = badges only, as before). */
export interface CampaignPromotion {
  homeBanner?: boolean;
  productBadge?: boolean;
  announcementBar?: boolean;
}

export const DEFAULT_PROMOTION: Required<CampaignPromotion> = { homeBanner: false, productBadge: true, announcementBar: false };

/** The promotion settings of an offer, with defaults for campaigns saved before the setting existed. */
export const promotionOf = (offer: Pick<Offer, "ui_content">): Required<CampaignPromotion> => ({
  ...DEFAULT_PROMOTION,
  ...((offer.ui_content as { promote?: CampaignPromotion } | null)?.promote ?? {}),
});

/** A cart hint pointing the shopper at a campaign they have partly filled or already qualify for. */
export interface CartNudge {
  offerId: string;
  slug: string;
  message: string;
}

/** One cart line as the nudges need it. */
export interface CartLineRef {
  variationId: string;
  quantity: number;
}

const discountText = (type: string, value: number, formatPrice: (n: number) => string) =>
  type === "percentage" ? `${value}% off` : `${formatPrice(value)} off`;

/**
 * Cart hints for live campaigns the merchant promotes with badges:
 * combo with some items in the cart; volume discount with a next tier to reach; buy-x-get-y with the buy
 * side partly or fully in the cart. Product-based rules are matched through the offer's variation_products
 * map (cart lines carry only variation ids); offers without it are skipped.
 */
export const cartNudges = (
  offers: LiveOffer[],
  lines: CartLineRef[],
  formatPrice: (amount: number) => string = (amount) => `TK ${amount}`,
  now = Date.now()
): CartNudge[] => {
  const byVariation = new Map<string, number>();
  for (const l of lines) byVariation.set(l.variationId, (byVariation.get(l.variationId) ?? 0) + l.quantity);
  const qtyOfProduct = (o: LiveOffer, productId: string) => {
    let q = 0;
    for (const [v, n] of byVariation) if (o.variation_products?.[v] === productId) q += n;
    return q;
  };

  return offers.flatMap((o): CartNudge[] => {
    if (!o.is_campaign || !o.slug || !isOfferLive(o, now) || !promotionOf(o).productBadge) return [];
    const title = o.ui_content?.page_title || o.name;
    const nudge = (message: string): CartNudge[] => [{ offerId: o._id, slug: o.slug, message }];

    if (o.type === "combo_fixed_price") {
      const r = o.offer_rules as OfferModel.ComboFixedPriceRules;
      const items = r.items ?? [];
      const missing = items.filter((i) => !byVariation.has(i.product_variation_id)).length;
      if (missing === 0 || missing === items.length) return [];
      return nudge(`Add ${missing} more item${missing === 1 ? "" : "s"} to complete the ${title} combo${r.price ? ` for ${formatPrice(r.price)}` : ""}`);
    }
    if (!o.variation_products) return [];

    if (o.type === "volume_discount") {
      const r = o.offer_rules as OfferModel.VolumeDiscountRules;
      const have = qtyOfProduct(o, r.product_id);
      if (have === 0) return [];
      const next = [...(r.tiers ?? [])].sort((a, b) => a.min_quantity - b.min_quantity).find((t) => t.min_quantity > have);
      if (!next) return nudge(`You get the best ${title} discount — order it from the offer page`);
      const more = next.min_quantity - have;
      return nudge(`Add ${more} more to get ${discountText(next.discount_type, next.discount_value, formatPrice)} with ${title}`);
    }
    if (o.type === "buy_x_get_y") {
      const r = o.offer_rules as OfferModel.BuyXGetYRules;
      const buy = r.buy_items ?? [];
      if (!buy.length) return [];
      const short = buy.map((b) => Math.max(0, (b.quantity || 1) - qtyOfProduct(o, b.product_id)));
      const needed = short.reduce((a, b) => a + b, 0);
      const started = buy.some((b) => qtyOfProduct(o, b.product_id) > 0);
      if (!started) return [];
      if (needed === 0) return nudge(`Your cart qualifies for ${title} — get your reward on the offer page`);
      return nudge(`Add ${needed} more item${needed === 1 ? "" : "s"} to get the ${title} reward`);
    }
    return [];
  });
};

/** "Runs 12 Jun, 10:00 – 20 Jun, 23:59" in the viewer's locale and time zone; a hint when dates are missing. */
export const describeCampaignWindow = (startsAt: string, endsAt: string, locale?: string): string => {
  const start = Date.parse(startsAt);
  const end = Date.parse(endsAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return "Set the start and end dates on the Offer step.";
  const fmt = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  return `Runs ${fmt.format(start)} – ${fmt.format(end)}`;
};
